type RefreshResponse = {
  accessToken?: string;
  refreshToken?: string;
};

let refreshPromise: Promise<string | null> | null = null;

function decodeJwtPayload(token: string): { exp?: number; iat?: number } | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function clearStoredSession(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
}

async function rotateRefreshToken(apiBaseUrl: string): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;

    const data = (await response.json()) as RefreshResponse;
    if (!data.accessToken || !data.refreshToken) return null;

    // Persist the pair together. The server consumes the old refresh token
    // atomically, so retaining it would make the next refresh look like replay.
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('refresh_token', data.refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

export async function ensureFreshAccessToken(
  apiBaseUrl: string,
): Promise<string | null> {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp || !payload?.iat) return token;

  const now = Math.floor(Date.now() / 1000);
  const halfLife = payload.iat + Math.floor((payload.exp - payload.iat) / 2);
  if (now < halfLife) return token;

  // One shared promise protects every CMS caller from replaying the same
  // single-use refresh token concurrently.
  refreshPromise ??= rotateRefreshToken(apiBaseUrl).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function revokeServerSession(apiBaseUrl: string): Promise<void> {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) return;

  try {
    await fetch(`${apiBaseUrl}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    // Local cleanup still happens if the API is unavailable.
  }
}
